import { useState } from 'react';

// Validation utility functions
export const validation = {
  // Email validation
  email: (email: string): string | null => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email';
    return null;
  },

  // Password validation
  password: (password: string): string | null => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
    return null;
  },

  // Confirm password validation
  confirmPassword: (password: string, confirmPassword: string): string | null => {
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return null;
  },

  // Name validation
  name: (name: string, fieldName: string = 'Name'): string | null => {
    if (!name) return `${fieldName} is required`;
    if (name.length < 2) return `${fieldName} must be at least 2 characters long`;
    if (name.length > 50) return `${fieldName} must be less than 50 characters`;
    if (!/^[a-zA-Z\s'-]+$/.test(name)) return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
    return null;
  },

  // Phone number validation - Irish format
  phone: (phone: string): string | null => {
    if (!phone) return 'Phone number is required';
    
    // Remove all spaces, hyphens, and parentheses for validation
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    
    // Irish phone number formats:
    // +353 followed by 8 or 9 digits (mobile: +353 8X XXX XXXX, landline: +353 1 XXX XXXX)
    // 0 followed by 8 or 9 digits (mobile: 08X XXX XXXX, landline: 01 XXX XXXX)
    const irishMobileRegex = /^(\+353|0)[1-9]\d{7,8}$/;
    
    if (!irishMobileRegex.test(cleanPhone)) {
      return 'Enter a valid Irish phone number (+353 or 08X format)';
    }
    
    // Additional length checks
    if (cleanPhone.startsWith('+353') && (cleanPhone.length < 12 || cleanPhone.length > 13)) {
      return 'Irish phone number with +353 should be 12-13 digits';
    }
    
    if (cleanPhone.startsWith('0') && (cleanPhone.length < 9 || cleanPhone.length > 10)) {
      return 'Irish phone number with 0 should be 9-10 digits';
    }
    
    return null;
  },

  // Required field validation
  required: (value: any, fieldName: string): string | null => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return `${fieldName} is required`;
    }
    return null;
  },

  // Text length validation
  textLength: (text: string, min: number, max: number, fieldName: string): string | null => {
    if (!text) return `${fieldName} is required`;
    if (text.length < min) return `${fieldName} must be at least ${min} characters long`;
    if (text.length > max) return `${fieldName} must be less than ${max} characters`;
    return null;
  },

  // Number validation
  number: (value: string | number, min?: number, max?: number, fieldName: string = 'Value'): string | null => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return `${fieldName} must be a valid number`;
    if (min !== undefined && num < min) return `${fieldName} must be at least ${min}`;
    if (max !== undefined && num > max) return `${fieldName} must be no more than ${max}`;
    return null;
  },

  // Price validation
  price: (price: string | number): string | null => {
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num)) return 'Please enter a valid price';
    if (num < 0) return 'Price cannot be negative';
    if (num > 10000) return 'Price cannot exceed €10,000';
    return null;
  },

  // URL validation
  url: (url: string, required: boolean = false): string | null => {
    if (!url && required) return 'URL is required';
    if (!url && !required) return null;
    
    try {
      new URL(url);
      return null;
    } catch {
      return 'Please enter a valid URL';
    }
  },

  // Date validation
  date: (date: string, fieldName: string = 'Date'): string | null => {
    if (!date) return `${fieldName} is required`;
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return `Please enter a valid ${fieldName.toLowerCase()}`;
    return null;
  },

  // Future date validation
  futureDate: (date: string, fieldName: string = 'Date'): string | null => {
    const dateError = validation.date(date, fieldName);
    if (dateError) return dateError;
    
    const dateObj = new Date(date);
    const now = new Date();
    if (dateObj <= now) return `${fieldName} must be in the future`;
    return null;
  },

  // Business name validation
  businessName: (name: string): string | null => {
    if (!name) return 'Business name is required';
    if (name.length < 2) return 'Business name must be at least 2 characters long';
    if (name.length > 100) return 'Business name must be less than 100 characters';
    return null;
  },

  // Address validation
  address: (address: string): string | null => {
    if (!address) return 'Address is required';
    if (address.length < 10) return 'Please enter a complete address';
    if (address.length > 200) return 'Address is too long';
    return null;
  },

  // Postal code validation (Irish format)
  postalCode: (code: string): string | null => {
    if (!code) return 'Postal code is required';
    // Irish Eircode format: A65 F4E2
    const irishPostalRegex = /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/i;
    if (!irishPostalRegex.test(code)) return 'Please enter a valid Irish postal code (e.g., D02 XY45)';
    return null;
  },

  // File validation
  file: (file: File | null, options: {
    required?: boolean;
    maxSize?: number; // in MB
    allowedTypes?: string[];
  } = {}): string | null => {
    const { required = false, maxSize = 5, allowedTypes = [] } = options;
    
    if (!file && required) return 'File is required';
    if (!file && !required) return null;
    
    if (file) {
      // Check file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSize) return `File size must be less than ${maxSize}MB`;
      
      // Check file type
      if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
        return `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`;
      }
    }
    
    return null;
  },

  // Rating validation
  rating: (rating: number): string | null => {
    if (rating < 1 || rating > 5) return 'Rating must be between 1 and 5 stars';
    return null;
  }
};

// Form validation helper
export function validateForm<T extends Record<string, any>>(
  data: T,
  rules: Record<keyof T, (value: any) => string | null>
): { isValid: boolean; errors: Record<keyof T, string> } {
  const errors = {} as Record<keyof T, string>;
  let isValid = true;

  for (const [field, rule] of Object.entries(rules)) {
    const error = rule(data[field as keyof T]);
    if (error) {
      errors[field as keyof T] = error;
      isValid = false;
    }
  }

  return { isValid, errors };
}

// Real-time validation hook
export function useFormValidation<T extends Record<string, any>>(
  initialData: T,
  rules: Record<keyof T, (value: any) => string | null>
) {
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<Record<keyof T, string>>({} as Record<keyof T, string>);
  const [touched, setTouched] = useState<Record<keyof T, boolean>>({} as Record<keyof T, boolean>);

  const validateField = (field: keyof T, value: any) => {
    const error = rules[field]?.(value);
    setErrors(prev => ({
      ...prev,
      [field]: error || ''
    }));
    return !error;
  };

  const updateField = (field: keyof T, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const touchField = (field: keyof T) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, data[field]);
  };

  const validateAll = () => {
    const { isValid, errors: allErrors } = validateForm(data, rules);
    const allTouched = Object.keys(data).reduce((acc, key) => ({ ...acc, [key]: true }), {} as Record<keyof T, boolean>);
    
    // Update both states in the same call to ensure synchronous update
    setErrors(allErrors);
    setTouched(allTouched);
    
    return isValid;
  };

  const reset = () => {
    setData(initialData);
    setErrors({} as Record<keyof T, string>);
    setTouched({} as Record<keyof T, boolean>);
  };

  return {
    data,
    errors,
    touched,
    updateField,
    touchField,
    validateAll,
    reset,
    isValid: Object.values(errors).every(error => !error)
  };
}